/**
 * The local-first wipe (M2, brief §2.5): how the Android build ends a session
 * without the network.
 *
 * `useAuth.signOut()` revokes the session on the server first
 * (`supabase.auth.signOut({ scope: 'global' })`). On an ordinary network error
 * auth-js returns a failure with the session still stored, because it deletes
 * the local session only after the server call succeeds or answers
 * 401/403/404 (installed `GoTrueClient.js`, `_signOut`). An idle logout that
 * used it would silently not happen offline. So every native expiry, and the
 * app lock's escape, uses this instead:
 *
 *   0. run the before-sign-out hooks (src/lib/signOutHooks.ts): the last
 *      authenticated calls, such as releasing this phone's push token (M8),
 *      need the session, so they go first. They are bounded and never throw,
 *      so offline they cost a couple of seconds and hold nothing back;
 *   1. wipe the secure auth keys and the session's prefs locally, first, and
 *      verify the keys are gone. A key the Keystore refuses to remove is
 *      marked stale: the client's adapter never returns it again, and every
 *      boot retries the removal (Codex on #1093), so the session cannot come
 *      back once the storage failure clears;
 *   2. reset the client's in-memory state: with storage empty,
 *      `signOut({ scope: 'local' })` finds no access token, makes no network
 *      call, and emits SIGNED_OUT;
 *   3. try the global revocation best-effort and ignore its failure. The
 *      server-side refresh token lapses on its own; this device no longer
 *      holds it.
 *
 * The web keeps `useAuth.signOut()` exactly as it is.
 */
import { supabase, SUPABASE_AUTH_KEY } from '@/integrations/supabase/client';
import { platform } from '@/platform';
import type { KeyValueStore } from '@/platform/types';
import {
  IDLE_DEADLINE_PREF,
  NATIVE_SESSION_PREFS,
  WIPE_PENDING_DEADLINE,
  authKeysFor,
} from '@/lib/nativeSessionKeys';
import { AuthWipeIncompleteError, incompleteWipeGuard, lastSeenAuthValue, removeAuthKeys } from '@/lib/nativeAuthStorage';
import { logger } from '@/lib/logger';
import { runBeforeSignOut } from '@/lib/signOutHooks';
import { wipeSessionData } from '@/lib/sessionWipe';
import { clearBookingDraft, flushBookingDraft } from '@/lib/bookingPersistence';
import { authStorage } from '@/platform/authStorage';
import { bindAuthReset } from '@/lib/authResetBinding';
import { underSessionStoreLock } from '@/lib/sessionStoreLock';

export interface WipeDeps {
  secure: KeyValueStore;
  prefs: KeyValueStore;
  authKey: string;
  client: Pick<typeof supabase, 'auth'>;
}

function accessTokenOf(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { access_token?: unknown };
    return typeof parsed.access_token === 'string' ? parsed.access_token : null;
  } catch {
    return null;
  }
}

/**
 * The auth session a stored session belongs to: its access token's
 * `session_id`. A refresh keeps it; a new sign-in, even to the same account,
 * gets a new one. Null when it cannot be told.
 */
export function sessionIdOf(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { access_token?: unknown };
    const payload = typeof parsed.access_token === 'string' ? parsed.access_token.split('.')[1] : undefined;
    if (!payload) return null;
    const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as { session_id?: unknown };
    return typeof claims.session_id === 'string' ? claims.session_id : null;
  } catch {
    return null;
  }
}

/** Test seam: the incomplete-wipe guard is module state. */
export function resetNativeSessionForTests() {
  incompleteWipeGuard.clear();
}

/**
 * Resolves true when the session is gone from secure storage, false when a key
 * survived but is durably unusable (stale, retried at every boot). Throws
 * AuthWipeIncompleteError when storage could record neither, AFTER resetting
 * the client, so the caller shows the user that sign-out did not complete.
 */
// One wipe at a time: the idle logout and the app lock can both end the same
// session at once (Codex on #1095); a second caller joins the first.
let wipeInFlight: Promise<boolean> | null = null;

export function localFirstWipe(deps?: WipeDeps): Promise<boolean> {
  if (wipeInFlight) return wipeInFlight;
  wipeInFlight = runLocalFirstWipe(deps).finally(() => {
    wipeInFlight = null;
  });
  return wipeInFlight;
}

async function runLocalFirstWipe(
  deps: WipeDeps = {
    secure: platform.storage.secure,
    prefs: platform.storage.prefs,
    authKey: SUPABASE_AUTH_KEY,
    client: supabase,
  },
): Promise<boolean> {
  // Every native end of a session (sign-out, idle expiry, the app lock's
  // escape and its fifth failure) comes through here, so the hooks run for
  // all of them, not only the Sign out button.
  await runBeforeSignOut();
  let token: string | null = null;
  let endedSession: string | null = null;
  // The ended session could not be read, even once more, and auth-js has not
  // read or written one this run: nothing then tells a refresh of it from a
  // new sign-in, so every session stored after counts as the ended one and
  // the sign-out goes through (Codex on #1136). Otherwise the session auth-js
  // last handled is the ended one, and a new sign-in still keeps its session
  // (Codex on #1136).
  let lineageUnknown = false;
  // A sign-in begun by link stores its PKCE verifier (and may store its user
  // record) before any session (Codex on #1136): the ended session's own
  // values are told apart from ones a new sign-in writes after the keys are
  // removed, which the wipe keeps.
  const pendingKeys = authKeysFor(deps.authKey).filter((key) => key !== deps.authKey);
  const endedValues = new Map<string, string | null>();

  // A session is the ended one when it is gone, holds the ended token, or
  // carries the ended session's id: auth-js may refresh the token while the
  // wipe runs, and that refresh is the same session, so it goes too (Codex
  // on #1136). A new sign-in has a new session id, the same account's
  // included, and stays (Codex on #1136).
  const isEndedSession = (raw: string | null) => {
    if (lineageUnknown) return true;
    const stored = accessTokenOf(raw);
    return stored === null || stored === token || (endedSession !== null && sessionIdOf(raw) === endedSession);
  };

  // What the ended session holds, and its removal, in one step under the
  // session store lock (Codex on #1137): a sign-in's write lands before it,
  // and is the session ended here, or after it, and is told apart below;
  // never between the read and the removal.
  let outcome: 'gone' | 'hidden' | 'incomplete' = 'incomplete';
  await underSessionStoreLock(async () => {
    let raw: string | null = null;
    try {
      raw = await deps.secure.get(deps.authKey);
    } catch {
      try {
        raw = await deps.secure.get(deps.authKey);
      } catch {
        const seen = lastSeenAuthValue(deps.authKey);
        if (seen === undefined) lineageUnknown = true;
        else raw = seen;
      }
    }
    token = accessTokenOf(raw);
    endedSession = sessionIdOf(raw);
    for (const key of pendingKeys) {
      try {
        endedValues.set(key, await deps.secure.get(key));
      } catch {
        endedValues.set(key, null);
      }
    }
    try {
      outcome = await removeAuthKeys(authKeysFor(deps.authKey), deps.secure, deps.prefs);
    } catch (e) {
      if (!(e instanceof AuthWipeIncompleteError)) throw e;
      outcome = 'incomplete';
    }
    // The booking draft names no account, so it goes here, in the same step
    // as the keys, before any check for a newer session (Codex on #1136): a
    // sign-in stored before the wipe below stops that wipe, and the client
    // reset then rejects it without a SIGNED_OUT event, so neither would
    // clear the draft, and the new account would inherit it.
    clearBookingDraft();
  });
  await flushBookingDraft().catch((e) => logger.warn('The booking draft did not settle after a local wipe', e));
  if (outcome === 'incomplete') {
    // Last resort: an expired deadline makes the next boot wipe again. If even
    // that write fails, the deadline already on disk is all that is left, so
    // the SIGNED_OUT cleanup below must not remove it (Codex on #1093).
    incompleteWipeGuard.mark();
    await deps.prefs.set(IDLE_DEADLINE_PREF, WIPE_PENDING_DEADLINE).catch(() => {});
    await Promise.allSettled(NATIVE_SESSION_PREFS.filter((k) => k !== IDLE_DEADLINE_PREF).map((k) => deps.prefs.remove(k)));
  } else {
    // This wipe worked: a guard kept for an earlier incomplete one is spent.
    incompleteWipeGuard.clear();
    await Promise.allSettled(NATIVE_SESSION_PREFS.map((k) => deps.prefs.remove(k)));
  }

  // Whatever else the session kept (drafts, kept lists, outboxes) goes with
  // it, by every path that ends it, not only the Sign out button (Codex on
  // #1134). Never throws. Only while no other session has been stored since
  // the keys were removed: a sign-in that lands in between (an auth link
  // exchanges its code directly) keeps its session and data (Codex on
  // #1136). A key that could not be removed still holds the ended token, so
  // it counts as ended. A store that cannot be read stops the wipe, as any
  // failed check does: it cannot tell a new session is not there (Codex on
  // #1136). The data left is the ended account's, which the outboxes drop at
  // their next drain and the next wipe clears. A sign-in begun since, which
  // has stored only its PKCE verifier so far, keeps it while the ended
  // account's data goes (Codex on #1136).
  await wipeSessionData(deps.secure, {
    stillEnded: async () => {
      return isEndedSession(await deps.secure.get(deps.authKey));
    },
    keepKeys: async () => {
      const keep: string[] = [];
      for (const key of pendingKeys) {
        const value = await deps.secure.get(key);
        if (value !== null && value !== endedValues.get(key)) keep.push(key);
      }
      return keep;
    },
  });

  // The client reset is bound to the ended session (Codex on #1136): a
  // sign-in that lands after the checks above keeps its session, here and on
  // the server, and a sign-in begun since keeps its verifier.
  try {
    await bindAuthReset(
      {
        isEnded: (key, value) =>
          value === null || (key === deps.authKey ? isEndedSession(value) : value === endedValues.get(key)),
      },
      () => deps.client.auth.signOut({ scope: 'local' }),
    );
  } catch (e) {
    logger.warn('Resetting the auth client after a local wipe failed', e);
  }

  if (token) {
    void deps.client.auth.admin.signOut(token, 'global').catch(() => {
      // Best effort only: offline is the case this path exists for.
    });
  }
  if (outcome === 'incomplete') throw new AuthWipeIncompleteError(authKeysFor(deps.authKey));
  return outcome === 'gone';
}

/**
 * Clears the session's prefs whenever the client signs out, by any path, so a
 * user-initiated sign-out cannot leave a deadline (or, later, the app-lock
 * setting) behind for the next account on the phone. Returns the unsubscribe.
 */
// The sign-out cleanup keeps an incomplete wipe's fallback deadline, or
// nothing would stop a surviving key coming back at the next boot.
async function clearSessionPrefs(prefs: KeyValueStore) {
  const others = NATIVE_SESSION_PREFS.filter((k) => k !== IDLE_DEADLINE_PREF).map((k) => prefs.remove(k));
  if (incompleteWipeGuard.active()) {
    await Promise.allSettled(others);
    return;
  }
  let current: string | null | undefined;
  try {
    current = await prefs.get(IDLE_DEADLINE_PREF);
  } catch {
    current = undefined; // unreadable
  }
  let deadline: Promise<unknown>;
  if (current === WIPE_PENDING_DEADLINE) {
    deadline = Promise.resolve();
  } else if (current === undefined) {
    // It may be the fallback, or the old account's own window. Replace it with
    // the fallback: already expired, so it cannot carry a window to the next
    // account, and a new session's write clears it (Codex on #1093).
    deadline = prefs.set(IDLE_DEADLINE_PREF, WIPE_PENDING_DEADLINE).catch(() => prefs.remove(IDLE_DEADLINE_PREF));
  } else {
    // A removal that fails must not leave the old window for the next account
    // (Codex on #1093): replace it with the expired fallback instead.
    deadline = prefs.remove(IDLE_DEADLINE_PREF).catch(() => prefs.set(IDLE_DEADLINE_PREF, WIPE_PENDING_DEADLINE));
  }
  await Promise.allSettled([deadline, ...others]);
}

/**
 * Also runs the session wipe on every SIGNED_OUT (Codex on #1134): some flows
 * end the session with a direct `supabase.auth.signOut()` (MFA removal, a
 * deactivated or deleted account's reactivation check) and never reach the
 * Sign out button's wipe or the local-first wipe, which would leave cached
 * bookings and outboxes in secure storage. After those wipes it finds
 * nothing left, so running it again is harmless. Mounted by the native shell
 * only; the website never subscribes.
 *
 * It runs in the background, so a new sign-in can complete before it ends
 * (Codex on #1134 and #1136). These wipes run one at a time, and each goes on
 * only while no session is stored: it never clears a replacement session or
 * its account's data. A sign-in under way, which has stored only its PKCE
 * verifier, keeps it while the ended account's data goes (signInKeysStored). Read through the auth adapter, a key marked stale or a
 * passed idle deadline counts as no session.
 */
// The SIGNED_OUT wipes, one after another (wipeSessionData never rejects).
let signedOutWipes: Promise<void> = Promise.resolve();

/**
 * The auth keys a sign-in begun since the session ended has stored: auth-js's
 * own sign-out removes the session, its PKCE verifier and its user record,
 * so any of them stored now belongs to a new sign-in, which a wipe keeps
 * while the ended account's data goes (Codex on #1136).
 */
export async function signInKeysStored(read: (key: string) => Promise<string | null>): Promise<string[]> {
  const keys = authKeysFor(SUPABASE_AUTH_KEY);
  const values = await Promise.all(keys.map((key) => read(key)));
  return keys.filter((_, i) => values[i] != null);
}

export function clearSessionPrefsOnSignOut(
  client: Pick<typeof supabase, 'auth'> = supabase,
  prefs: KeyValueStore = platform.storage.prefs,
  secure: KeyValueStore = platform.storage.secure,
  sessionStored: () => Promise<boolean> = async () => (await authStorage.getItem(SUPABASE_AUTH_KEY)) != null,
  keepKeys: () => Promise<readonly string[]> = () => signInKeysStored(async (key) => authStorage.getItem(key)),
): () => void {
  const stillEnded = async () => !(await sessionStored());
  const { data } = client.auth.onAuthStateChange((event) => {
    if (event !== 'SIGNED_OUT') return;
    // The booking draft is the one thing kept that names no account: it goes
    // now, while the session that ended is the only one, so a sign-in that
    // completes before the wipe below runs cannot inherit it. That wipe
    // stops for such a sign-in (Codex on #1136).
    clearBookingDraft();
    // Outside the callback: auth-js warns against awaiting inside it.
    setTimeout(() => {
      void clearSessionPrefs(prefs);
      signedOutWipes = signedOutWipes.then(() => wipeSessionData(secure, { stillEnded, keepKeys }));
    }, 0);
  });
  return () => data.subscription.unsubscribe();
}
